import { useState } from "react";
import {
    Box,
    Container,
    Heading,
    SimpleGrid,
    Image,
    Badge,
    Text,
    Flex,
    Button,
    Skeleton,
    Menu,
    MenuButton,
    IconButton,
    MenuList,
    MenuItem,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay, useColorModeValue,
} from "@chakra-ui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { HamburgerIcon } from "@chakra-ui/icons";
import { z } from "zod";
import { useRef } from "react";

import { RoomService, CompanyService, type RoomPublic, type Company } from "../../client";
import AddRoom from "../../components/Rooms/AddRoom";
import EditRoom from "../../components/Rooms/EditRoom";
import useCustomToast from "../../hooks/useCustomToast.ts";
import useLicenseActionGuard from "../../hooks/useLicenseActionGuard";
import useAuth from "../../hooks/useAuth.ts";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/Common/Navbar.tsx";



// 🔹 Типізація кімнат
export interface RoomType {
    ID: string;
    name_room: string;
    description: string;
    image: string;
    status: boolean;
    is_channel: boolean;
    owner_id: string;
    company_id?: string;
    department_id?:string;

}

// 🔹 Схема валідації URL-параметрів
const roomsSearchSchema = z.object({
    page: z.number().catch(1),
});

export const Route = createFileRoute("/_layout/rooms")({
    component: Room,
    validateSearch: (search) => roomsSearchSchema.parse(search),
});

const PER_PAGE = 6;

function geRoomQueryOptions({ page }: { page: number }) {
    return {
        queryFn: () =>
            RoomService.readRooms({ skip: (page - 1) * PER_PAGE, limit: PER_PAGE }),
        queryKey: ["rooms", { page }],
    };
}

function RoomGrid({ onDeleteRoom, onEditRoom }: { onDeleteRoom: (room: RoomType) => void; onEditRoom: (room: RoomType) => void }) {
    const { page } = Route.useSearch();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const { data: rooms, isPending } = useQuery({
        ...geRoomQueryOptions({ page }),
    });

    // Завантаження списку компаній (для відображення назв компанії/департаменту на картці)
    const { data: companiesData } = useQuery({
        queryKey: ["companies"],
        queryFn: () => CompanyService.getCompanies(),
    });

    const raw = rooms as any;
    const roomsArray: RoomPublic[] = Array.isArray(raw?.Data)
        ? raw.Data
        : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw)
                ? raw
                : [];

    const transformedRooms: RoomType[] = roomsArray.map((room: RoomPublic) => ({
        ID: room.ID,
        name_room: room.name_room,
        description: room.description || "No description available",
        image: room.image || "",
        status: room.status ?? false,
        is_channel: room.is_channel ?? false,
        owner_id: room.owner_id,
        company_id: room.company_id || "",
        department_id: room.department_id || ""
    }));

    queryClient.setQueryData(["rooms"], transformedRooms);

    return (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={8} mt={6}>
            {isPending
                ? [...Array(8)].map((_, index) => (
                    <Skeleton key={index} height="320px" borderRadius="2xl" />
                ))
                : transformedRooms.length > 0 ? (
                    transformedRooms.map((room) => (
                        <RoomCard
                            key={room.ID}
                            room={room}
                            companies={(companiesData as Company[]) || []}
                            onDelete={() => onDeleteRoom(room)}
                            onEdit={() => onEditRoom(room)}
                        />
                    ))
                ) : (
                    <Flex direction="column" align="center" justify="center" gridColumn="1/-1" py={20}>
                        <Text fontSize="lg" color="gray.500" fontWeight="medium">
                            {t("rooms.noRooms")}
                        </Text>
                    </Flex>
                )}
        </SimpleGrid>
    );
}

function RoomCard({ room, onDelete, onEdit, companies = [] as Company[] }: { room: RoomType; onDelete: () => void; onEdit: () => void; companies?: Company[] }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.100", "gray.700");
    const boxShadow = useColorModeValue(
        "0 4px 20px rgba(0, 0, 0, 0.05)",
        "0 4px 20px rgba(0, 0, 0, 0.4)"
    );

    const { t } = useTranslation();
    const roomGuard = useLicenseActionGuard("admin_write");

    const isOwner = room.owner_id === user?.ID

    const handleOpenChat = () => {
        navigate({ to: `/chat/${room.ID}` });
    };

    const company = room.company_id ? companies.find((c) => c.ID === room.company_id) : undefined;
    const companyName = company?.name;
    const departmentName = room.department_id
        ? company?.departments?.find((d) => d.ID === room.department_id)?.name
        : undefined;

    return (
        <Box 
             borderRadius="2xl"
             overflow="hidden"
             boxShadow={boxShadow}
             bg={cardBg}
             border="1px solid"
             borderColor={borderColor}
             position="relative"
             _hover={{ transform: "translateY(-6px)", boxShadow: "2xl", borderColor: "ui.main" }}
             transition="all 0.3s cubic-bezier(.08,.52,.52,1)"
             cursor="pointer"
             onClick={handleOpenChat}
             w="100%"
             maxW="450px"
             mx="auto"
        >
            <Box position="relative" h="180px">
                <Image src={room.image}
                       alt={room.name_room}
                       objectFit="cover"
                       h="100%"
                       w="100%"
                       fallbackSrc="https://via.placeholder.com/400x200?text=Room"
                />
                <Box 
                    position="absolute" 
                    top={0} left={0} right={0} bottom={0} 
                    bgGradient="linear(to-t, blackAlpha.600, transparent)" 
                />
                
                <Flex position="absolute" bottom={3} left={3} gap={2}>
                    <Badge borderRadius="full" px="3" py="0.5" colorScheme={room.status ? "green" : "red"} variant="solid" fontSize="10px">
                        {room.status ? t("rooms.active") : t("rooms.inactive")}
                    </Badge>
                    {room.is_channel && (
                        <Badge variant="solid" borderRadius="full" px="3" py="0.5" colorScheme="orange" fontSize="10px">
                            {t("rooms.channel")}
                        </Badge>
                    )}
                </Flex>

                <Box position="absolute" top={2} right={2} zIndex={10}>
                    <Menu isLazy>
                        <MenuButton
                            as={IconButton}
                            aria-label="Options"
                            icon={<HamburgerIcon />}
                            variant="ghost"
                            color="white"
                            size="sm"
                            _hover={{ bg: "whiteAlpha.300" }}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <MenuList onClick={(e) => e.stopPropagation()} borderRadius="xl" shadow="xl" border="none">
                            <MenuItem icon={<HamburgerIcon />} onClick={onEdit} isDisabled={roomGuard.disabled || (!isOwner && !user?.isSuperAdmin)} title={roomGuard.reason}>{t("rooms.update")}</MenuItem>
                            <MenuItem onClick={onDelete} isDisabled={roomGuard.disabled || (!isOwner && !user?.isSuperAdmin)} color="red.500" title={roomGuard.reason}>
                                {t("rooms.delete")}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Box>
            </Box>

            <Box p={5}>
                <Text fontWeight="bold" fontSize="xl" noOfLines={1} color={useColorModeValue("gray.800", "white")} mb={1}>
                    {room.name_room}
                </Text>

                <Text fontSize="sm" color="gray.500" noOfLines={2} h="40px" mb={4}>
                    {room.description}
                </Text>

                {(companyName || departmentName) && (
                    <Flex align="center" gap={3} pt={4} borderTop="1px solid" borderColor={borderColor}>
                        <Box flex={1}>
                            {companyName && (
                                <Text fontSize="xs" color="ui.main" fontWeight="bold" isTruncated>
                                    {companyName}
                                </Text>
                            )}
                            {departmentName && (
                                <Text fontSize="xs" color="gray.500" isTruncated>
                                    {departmentName}
                                </Text>
                            )}
                        </Box>
                        <Button size="sm" variant="ghost" colorScheme="orange" fontSize="xs">
                            {t("rooms.view")}
                        </Button>
                    </Flex>
                )}
            </Box>
        </Box>
    );
}

function Room() {
    const cancelRef = useRef<HTMLButtonElement | null>(null);
    const { user } = useAuth();
    const [isEditRoomOpen, setIsEditRoomOpen] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedRoom, setSelectedRoom] = useState<RoomType | null>(null);
    const queryClient = useQueryClient();
    const showToast = useCustomToast();
    const { t } = useTranslation();
    const roomGuard = useLicenseActionGuard("admin_write");

    const deleteMutation = useMutation({
        mutationFn: async (roomId: string) => {
            await RoomService.deleteRoom(roomId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["rooms"] });
            showToast("Success!", t("rooms.deleteSuccess"), "success");
            onClose();
        },
        onError: (error: any) => {
            console.error("Error deleting room:", error);

            if (error?.status === 401) {
                showToast(t("rooms.unauthorized"), t("rooms.unauthorized"), "error");
            } else {
                showToast("Error", error.message || t("rooms.deleteError"), "error");
            }
        },
    });

    const handleDeleteRoom = (room: RoomType) => {
        setSelectedRoom(room);
        onOpen();
    };

    const confirmDelete = () => {
        if (roomGuard.disabled) {
            showToast("Unavailable", roomGuard.reason || t("common.actionBlocked", "Action is unavailable"), "error");
            return;
        }
        if (selectedRoom) {
            deleteMutation.mutate(selectedRoom.ID);
        }
    };

    const handleEditRoom = (room: RoomType) => {
        setSelectedRoom(room);
        setIsEditRoomOpen(true);
    };

    return (
        <Box pt={4}>
            <Container maxW="full" px={8}>
                <Flex direction="column" gap={6}>
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Heading size="lg" fontWeight="black" letterSpacing="tight">
                                {t("rooms.heading")}
                            </Heading>
                            <Text color="gray.500" fontSize="sm" mt={1}>
                                Manage your communication channels
                            </Text>
                        </Box>
                        <Navbar
                            type={t("rooms.add")}
                            addModalAs={AddRoom}
                            isDisabled={roomGuard.disabled || (!user?.isSuperAdmin && !user?.isAdmin)}
                        />
                    </Flex>

                    <RoomGrid onDeleteRoom={handleDeleteRoom} onEditRoom={handleEditRoom} />
                </Flex>

                <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
                    <AlertDialogOverlay backdropFilter="blur(4px)">
                        <AlertDialogContent borderRadius="2xl">
                            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                                {t("rooms.deleteConfirmTitle")}
                            </AlertDialogHeader>

                            <AlertDialogBody color="gray.600">
                                {t("rooms.deleteConfirmBody")}
                            </AlertDialogBody>

                            <AlertDialogFooter gap={3}>
                                <Button ref={cancelRef} onClick={onClose} variant="ghost">
                                    {t("common.cancel", "Cancel")}
                                </Button>
                                <Button colorScheme="red" onClick={confirmDelete} borderRadius="xl" isDisabled={roomGuard.disabled} title={roomGuard.reason}>
                                    {t("rooms.delete")}
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>
                {selectedRoom && <EditRoom room={selectedRoom} isOpen={isEditRoomOpen} onClose={() => setIsEditRoomOpen(false)} />}
            </Container>
        </Box>
    );
}
